import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { readClientSession } from "utils/clientSession";

const TICKET_CATEGORIES = [
  { value: "onboarding", label: "Onboarding & setup" },
  { value: "technical", label: "Technical / copy-trading" },
  { value: "billing", label: "Billing & subscription" },
  { value: "account", label: "Account & login" },
  { value: "other", label: "Other" },
];

const TELEGRAM_SUPPORT_URL =
  (process.env.REACT_APP_TELEGRAM_SUPPORT_URL || "").trim() ||
  "https://t.me/AlphaSniperAdmin";

const labelClass =
  "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400";
const inputClass =
  "mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-navy-800 outline-none transition placeholder:text-gray-400 focus:border-brand-400 focus:ring-2 focus:ring-brand-400/25 dark:border-white/15 dark:bg-navy-900 dark:text-white dark:placeholder:text-gray-500";

function formatDisplayName(session) {
  const first = String(session?.firstName || "").trim();
  const last = String(session?.lastName || "").trim();
  if (first && last) return `${first} ${last}`;
  return first || last || "";
}

function SupportSubnav({ active }) {
  const linkClass = (key) =>
    `rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
      active === key
        ? "bg-brand-500 text-white dark:bg-brand-400"
        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
    }`;

  return (
    <nav className="flex flex-wrap gap-2" aria-label="Support sections">
      <Link to="/client/support" className={linkClass("help")}>
        Help & tickets
      </Link>
      <Link to="/client/support/suggestions" className={linkClass("suggestions")}>
        Suggestions
      </Link>
    </nav>
  );
}

export default function SupportHelp() {
  const session = readClientSession();
  const clientId =
    session?.clientId != null ? String(session.clientId) : "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("technical");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const display = formatDisplayName(session);
    if (display) setName(display);
    if (session?.email) setEmail(String(session.email).trim());
  }, [session]);

  const defaultSubjectHint = useMemo(() => {
    if (!clientId) return "Short summary of your request";
    return `e.g. Issue with slot / copy settings (ID ${clientId})`;
  }, [clientId]);

  async function submitTicket(e) {
    e.preventDefault();
    setStatus("sending");
    setFeedback(null);
    try {
      const body = {
        name: name.trim(),
        email: email.trim(),
        category,
        subject: subject.trim(),
        message: message.trim(),
        source: "client-dashboard",
      };
      if (clientId) body.clientId = clientId;

      const res = await fetch(apiUrl("/api/contact-ticket"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }
      if (!res.ok || !data.ok) {
        const text = clientSafeMessage(
          data.message,
          res.status === 503
            ? "Support is temporarily unavailable. Try Telegram or contact us from the public site."
            : "Could not send your ticket. Please try again.",
        );
        setFeedback({ type: "error", text });
        setStatus("idle");
        return;
      }
      setFeedback({ type: "success", text: data.message });
      setSubject("");
      setMessage("");
      setStatus("idle");
    } catch {
      setFeedback({
        type: "error",
        text: "Network error. Check your connection and try again.",
      });
      setStatus("idle");
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <SupportSubnav active="help" />
        <h2 className="mt-4 text-xl font-bold text-navy-700 dark:text-white">
          Support & help
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
          Open a support ticket below. We usually reply within one business day.
          For quick questions you can also message us on Telegram.
        </p>
        {clientId ? (
          <p className="mt-3 inline-flex rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-bold text-brand-800 dark:bg-brand-500/15 dark:text-brand-200">
            Your client ID: {clientId} — included automatically in your ticket.
          </p>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Link
            to="/FrequentlyAskedQuestions"
            className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            FAQ
          </Link>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <Link
            to="/client/billing"
            className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Billing & payments
          </Link>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <Link
            to="/client/subscription"
            className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Subscription
          </Link>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <Link
            to="/client/support/suggestions"
            className="font-semibold text-brand-500 hover:underline dark:text-brand-300"
          >
            Suggestions
          </Link>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_minmax(240px,280px)]">
        <section className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Open a support ticket
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Do not send trading passwords or private keys. Describe the issue
            and which slot or account is affected.
          </p>

          {feedback ? (
            <div
              className={`mt-4 rounded-xl border px-4 py-3 text-sm font-semibold ${
                feedback.type === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-200"
                  : "border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200"
              }`}
              role="status"
            >
              {feedback.text}
            </div>
          ) : null}

          <form className="mt-6 space-y-4" onSubmit={submitTicket} noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="support-name">
                  Your name
                </label>
                <input
                  id="support-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  minLength={2}
                  maxLength={200}
                  value={name}
                  onChange={(ev) => setName(ev.target.value)}
                  className={inputClass}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="support-email">
                  E-mail
                </label>
                <input
                  id="support-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label className={labelClass} htmlFor="support-category">
                Topic
              </label>
              <select
                id="support-category"
                name="category"
                value={category}
                onChange={(ev) => setCategory(ev.target.value)}
                className={`${inputClass} cursor-pointer`}
              >
                {TICKET_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass} htmlFor="support-subject">
                Subject
              </label>
              <input
                id="support-subject"
                name="subject"
                type="text"
                required
                minLength={4}
                maxLength={180}
                value={subject}
                onChange={(ev) => setSubject(ev.target.value)}
                className={inputClass}
                placeholder={defaultSubjectHint}
              />
            </div>

            <div>
              <label className={labelClass} htmlFor="support-message">
                Message
              </label>
              <textarea
                id="support-message"
                name="message"
                required
                minLength={20}
                maxLength={8000}
                rows={6}
                value={message}
                onChange={(ev) => setMessage(ev.target.value)}
                className={`${inputClass} min-h-[140px] resize-y`}
                placeholder="Describe what you need help with (slot number, error messages, screenshots if any — never passwords)."
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={status === "sending"}
                className="rounded-xl bg-brand-500 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
              >
                {status === "sending" ? "Sending…" : "Send ticket"}
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-500">
                We use your details only to respond to this request.
              </span>
            </div>
          </form>
        </section>

        <aside className="space-y-4">
          <div className="rounded-[20px] border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Telegram
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Faster help for urgent setup or billing questions.
            </p>
            <a
              href={TELEGRAM_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex w-full items-center justify-center rounded-xl border-2 border-brand-500 bg-brand-500 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-brand-600 dark:border-brand-400 dark:bg-brand-400"
            >
              Message support
            </a>
          </div>
          <div className="rounded-[20px] border border-dashed border-gray-200 bg-gray-50/80 p-5 dark:border-white/10 dark:bg-navy-900/40">
            <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
              <strong className="text-navy-800 dark:text-white">Tips:</strong>{" "}
              include your trading account ID (e.g. 426052S1), broker platform,
              and whether the issue is about payment, connection, or risk
              settings.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
